// BeeNest i18n — warm, human, never robotic.
// Use t(language, key) anywhere. Falls back to English if the key is missing.

const STRINGS = {
  en: {
    // Branding
    appName: "BeeNest",
    tagline: "A peaceful little hive for safe learning and emotional growth.",

    // Auth
    getStarted: "Begin Gently",
    signIn: "Sign In",
    createAccount: "Create Account",
    email: "Email",
    password: "Password",
    name: "Your Name",
    welcome: "Welcome back",
    welcomeChild: "Welcome back, little bee",
    alreadyInHive: "Already in the hive? ",
    newHere: "New here? ",
    softWelcome: "A gentle nest for your family.",
    softReturn: "Step softly back in.",
    forgotPasswordLink: "Forgot your password?",
    forgotTitle: "A gentle reset path",
    forgotBody: "Share your email and we'll quietly prepare a reset link.",
    forgotSubmit: "Send the soft reset",
    forgotSending: "Preparing...",
    forgotSuccess: "If that email is in our hive, we've prepared a calming reset path.",
    forgotBackToSignIn: "Back to sign in",
    resetTitle: "Choose a new password",
    resetBody: "Pick something easy for you to remember. At least 8 soft characters.",
    resetNewPassword: "New password",
    resetSubmit: "Settle in",
    resetSubmitting: "Settling...",
    resetSuccess: "Your password has settled softly. Sign in when you're ready.",
    resetInvalid: "This reset link has rested. Please request a new one.",
    resetGoSignIn: "Go to sign in",

    // Dashboard / Nav
    parentDashboard: "Parent Nest",
    childProfiles: "Child Profiles",
    addChild: "Add a child",
    todaysFocus: "Today's gentle focus",
    settings: "Settings",
    membership: "Membership",
    familyLetters: "Family Reflection Letters",
    logout: "Sign out",
    chooseChild: "Choose a profile",
    progress: "Progress",
    insights: "Wellness Insights",
    day: "Day",
    night: "Night",
    toggleBedtime: "Toggle bedtime mode",
    languageMenu: "Language",

    // Dashboard page
    dashChildren: "Children",
    dashActivities: "Activities",
    dashMinutes: "Minutes",
    dashFlowers: "Flowers",
    dashRecentCheckins: "Recent Check-ins",
    dashNoCheckins: "No check-ins yet — feelings will gather here.",
    dashNoChildren: "No little ones yet. Add a profile to begin gently.",
    dashAgesPrefix: "Ages",
    dashFlowersSuffix: "flowers",
    dashRhythmsLabel: "Hive Rhythms",
    dashRhythmsCount: "calming moments tended this week",
    dashRhythmsOpen: "open gentle reflection →",
    dashSensoryTitle: "Sensory & Bedtime Settings",
    dashSensoryBody: "Adjust sound, motion, contrast, and bedtime mode.",
    dashLettersTitle: "Family Reflection Letters",
    dashLettersBody: "A soft monthly note from BeeNest about your gentle rhythms.",
    dashMembershipBody: "Support BeeNest and unlock the full hive.",

    // Pause invite (dashboard)
    pauseInviteTag: "For you",
    pauseInviteBody: "Your hive has been gathering quiet moments. Would you like a small pause to reflect?",
    pauseInviteBegin: "Begin softly",
    pauseInviteLater: "Maybe later",

    // Profiles page
    profilesTitle: "Child Profiles",
    profilesSubtitle: "Each profile holds its own gentle pace.",
    profilesAddAnother: "Add another little bee",
    profilesNewBee: "A new little bee",
    profilesNameLabel: "Name",
    profilesAgeLabel: "Age range",
    profilesInterestsLabel: "Interests (comma separated)",
    profilesInterestsPlaceholder: "bees, stories, painting",
    profilesLanguageLabel: "Language",
    profilesCancel: "Cancel",
    profilesSave: "Save gently",
    profilesSaving: "Saving...",
    profilesNewBeeAdded: "New little bee added ✿",
    profilesSaveFailed: "Couldn't save profile",
    profilesRemoved: "Profile removed gently",
    profilesEditBedtime: "Edit bedtime routine",
    profilesEditSensory: "Edit sensory profile",
    profilesRemove: "Remove profile",
    bedtimeDialogTitle: "Bedtime routine",
    bedtimeDialogIntro: "We'll quietly suggest the bedtime hive 30 minutes before, and during the window.",
    bedtimeEnableLabel: "Enable bedtime routine",
    bedtimeEnableHint: "Soft wind-down banner on the home screen",
    bedtimeStartTime: "Start time",
    bedtimeEndTime: "End time",
    bedtimeSaved: "Bedtime routine saved ✿",
    bedtimeSaveFailed: "Couldn't save just yet",
    sensoryDialogTitle: "Sensory profile",
    sensoryDialogIntro: 'Each child can have their own sensitivities. "Inherit" uses parent defaults.',
    sensorySaved: "Sensory profile saved ✿",
    sensorySound: "Sound",
    sensoryMotion: "Animation",
    sensoryContrast: "Contrast",
    sensoryFont: "Font",
    sensoryInherit: "Inherit",
    sensoryMute: "Mute",
    sensoryLow: "Low",
    sensoryNormal: "Normal",
    sensoryReduced: "Reduced",
    sensoryFull: "Full",
    sensoryStandard: "Standard",
    sensoryHigh: "High",
    sensoryDyslexia: "Dyslexia-friendly",

    // HiveReset page
    hiveResetTag: "Hive Reset",
    hiveResetExit: "Exit ritual",
    hiveResetBreathIn: "Breathe in slowly...",
    hiveResetHold: "Hold gently...",
    hiveResetBreathOut: "Breathe out softly...",
    hiveResetBreathHelp: "Two slow cycles · then a gentle story",
    hiveResetSkipBreath: "Skip to story",
    hiveResetWeaving: "Weaving a quiet story for tonight...",
    hiveResetStoryResting: "The story is resting tonight.",
    hiveResetGoBedtime: "Go to Bedtime",
    hiveResetSleepTight: "Sleep tight ✿",
    hiveResetSleepTightHeading: "Sleep tight, little bee",
    hiveResetHiveQuiet: "The hive is quiet now.",
    hiveResetBackHome: "Back to home",
    hiveResetAnother: "Another quiet story",

    // Rhythms page
    rhythmsBack: "Back to nest",
    rhythmsLabel: "Hive Rhythms",
    rhythmsHeading: "Moments of Calm",
    rhythmsSoftMoment: "soft moment",
    rhythmsSoftMoments: "soft moments",
    rhythmsBedtimeTitle: "Bedtime stories shared",
    rhythmsBedtimeBody: "this week — every story rests in your hive.",
    rhythmsFeelingsTitle: "Feelings shared",
    rhythmsFeelingsEmpty: "When ready, feelings will gather here softly.",
    rhythmsCheckins: "check-ins · most often",
    rhythmsMomentsTitle: "Calming moments",
    rhythmsMomentsBody: "gentle moments tended this week.",
    rhythmsDaysTitle: "Days with calm",
    rhythmsDaysBody: "days this week held a calming pause. Rest is part of your rhythm too.",
    rhythmsFavoritesTitle: "Your favorite calm spaces",
    rhythmsFavoritesEmpty: "When your child rests in any calming space, it will softly show up here.",
    rhythmsFooter: "Progress is gentle and non-linear. Your hive is always here — without pressure, without scoring.",
    rhythmsMoment: "moment",
    rhythmsMoments: "moments",
    feelJoyful: "joyful",
    feelCalmAdj: "calm",
    feelRested: "rested",
    feelTender: "tender",
    feelHonest: "honest",
    feelLively: "lively",

    // Letters page
    lettersBack: "Back to nest",
    lettersTag: "Family Reflection Letters",
    lettersHeading: "Kind notes from the hive",
    lettersSubtitle: "One soft monthly letter per child — a thoughtful friend's reflection on your gentle rhythms.",
    lettersComposeMonth: "Compose this month's letter",
    lettersCompose: "Compose",
    lettersComposing: "Composing...",
    lettersReady: "Letter ready ✿",
    lettersComposed: "A gentle letter has been composed ✿",
    lettersResting: "The letter is resting tonight. Try again gently.",
    lettersEmpty: "No letters yet. Compose your first one above when you're ready.",

    // Pause page
    pauseReturn: "Return softly",
    pauseLabel: "Gentle Reflection Pause",
    pauseWelcomeHeading: "A quiet breath, just for you",
    pauseWelcomeBody: "You have been caring for your hive. This moment is for you too. Take 60–90 soft seconds — return whenever you are ready.",
    pauseLater: "Maybe later",
    pauseBegin: "Begin softly",
    pauseSettleIn: "Settle in softly...",
    pauseBreathIn: "Breathe in slowly...",
    pauseHold: "Hold gently...",
    pauseBreathOut: "Breathe out softly...",
    pauseBreathHelper: "Soft bees humming · gentle moonlight",
    pauseGathering: "Gathering a quiet thought for you...",
    pauseFallback: "Whatever this day has held, you have shown up softly. Rest is also part of nurturing.\n\nYour hive benefits from your softness too.",
    pauseContinue: "Continue",
    pauseCloseHeading: "You deserve softness too",
    pauseCloseBody: "The hive is gentle, and so are you.",

    // Calm Sounds page
    soundsBack: "Back home",
    soundsTitle: "Calm sounds",
    soundsSubtitle: "Choose a gentle ambience. Tap again to rest the sound.",
    soundsMutedTitle: "Sounds are resting in this hive.",
    soundsMutedBody: "Sensory sound level is set to Mute. Adjust in Settings or this child's sensory profile.",
    soundsVolumeLabel: "Volume",
    soundsVolumeCapped: "· capped (sensory low)",
    soundsFooter: "All sounds are gently woven by the app — never realistic, never sharp.",
    soundRain: "Soft rain",
    soundForest: "Forest hush",
    soundBees: "Bees humming",
    soundWind: "Gentle wind",
    soundOcean: "Ocean breath",

    // Child experience
    bedtime: "Bedtime",
    feelings: "Feelings",
    breathing: "Breathing",
    breathHint: "Breathe in slowly... and out softly",
    howFeel: "How are you feeling, little one?",
    feelHappy: "Happy",
    feelCalm: "Calm",
    feelTired: "Tired",
    feelSad: "Sad",
    feelMad: "Frustrated",
    feelExcited: "Excited",
    save: "Save gently",
    listen: "Listen",
    newStory: "Weave a new story",
    backHome: "Back home",
    minutes: "minutes",
    flowers: "flowers grown",
    activities: "Activities",
    garden: "Your Garden",

    // Settings labels
    soundLevel: "Sound level",
    motion: "Animation",
    contrast: "Contrast",
    font: "Font",
    bedtimeMode: "Bedtime mode",
    language: "Language",

    // Soft system messages
    loadingSoftly: "Settling in softly...",
    softError: "Rest a moment and try again.",
    somethingGentlyWrong: "Something went gently wrong",
    voiceResting: "Voice is resting softly. Try again in a moment.",
    storyResting: "The story is resting. Please try again gently.",
    letterResting: "The letter is resting tonight. Please try again gently.",
    welcomeToHive: "Welcome to the hive ✿",
    welcomeBackSoft: "Welcome back ✿",
    sessionRested: "Your session needed a quiet rest. Please sign in again.",

    // Footer
    footer: "BeeNest™ by IM•BEE — built gently, with care for every little heart.",
    footerAttribution: "concept, mascot, and educational system by Evelyn Villanueva. All rights reserved.",
    footerPrivacy: "Privacy",
    footerTerms: "Terms",

    // Auth surface polish
    authRegisterSub: "A gentle nest for your family.",
    authLoginSub: "Step softly back in.",
    authWelcomeIn: "Welcome to the hive ✿",
    authWelcomeBack: "Welcome back ✿",
    authSoftError: "Something went gently wrong. Please try again.",
    passwordShow: "Show password",
    passwordHide: "Hide password",
    forgotDevModeLabel: "Dev mode reset link (no email provider configured)",

    // Onboarding / Profiles warmth
    profilesAddFirstBee: "Add your first little bee",

    // Discover safety + moderation
    discoverFaceGently: "Let's keep faces and selfies out of the discovery hive. Try pointing at an object, a plant, or something calm in the room.",
    discoverUnsafeGently: "Let's choose a softer thing to discover together — a leaf, a toy, a fruit, or something kind in the room.",
    discoverNoCamera: "This little browser is shy about cameras. Upload a photo instead, and we'll discover it together.",

    // Privacy page
    privacyTitle: "Privacy, kept softly",
    privacyIntro: "We built BeeNest for tired families and sensitive little hearts. Here is how we hold your information — quietly, with care.",
    privacyH1: "What we collect",
    privacyP1: "Only what we truly need: your email and a hashed password to sign you in, your child's first name and age range so the experience can shape itself, and the gentle activities you choose inside the hive. No tracking pixels. No ad networks. No social plugins.",
    privacyH2: "What we don't collect",
    privacyP2: "We do not collect your child's full name, photos, audio recordings, voice samples, location, contacts, or device identifiers. Photos used in Discover are processed briefly and never stored as images on our servers — only the resulting calm sentence is saved if you choose to keep it.",
    privacyH3: "AI and your data",
    privacyP3: "When you use a generative feature (a bedtime story, a reflection letter, an object discovery), the necessary context is sent securely to a model provider so the response can be generated. Prompts are scrubbed of personally identifying details. We do not allow your data to train third-party models.",
    privacyH4: "Children's safety",
    privacyP4: "BeeNest is COPPA-conscious. Accounts are created by the caregiver, not the child. Child profiles never have their own login, password, or contact information. All settings are managed by the caregiver.",
    privacyH5: "How to reach us",
    privacyP5: "Questions, requests to delete your account, or anything else — write to us through the support channel inside Settings. We answer in plain language, never in legalese.",

    // Terms page
    termsTitle: "A gentle agreement",
    termsIntro: "These are the soft promises we make together when you use BeeNest. They aren't legalese — they're a calm agreement between you and us.",
    termsH1: "What BeeNest is",
    termsP1: "BeeNest is a calm, sensory-friendly learning and wellness companion for caregivers and children ages 3 to 10. It is not a medical service, a diagnostic tool, or a substitute for professional care.",
    termsH2: "Who can use it",
    termsP2: "Accounts are created by a parent or caregiver, age 18 or older. Children use the experience with a caregiver's supervision and permission.",
    termsH3: "How we keep it calm",
    termsP3: "We do not show ads. We do not gamify engagement. We do not use streaks, leaderboards, or push notifications designed to pull anyone back in. If something feels too stimulating, you can pause or close it at any time.",
    termsH4: "Your content",
    termsP4: "Your child's progress, check-ins, discoveries, and saved letters belong to your family. You can delete them at any time from Settings. We will never sell or share this content.",
    termsH5: "If something changes",
    termsP5: "If we ever need to update these terms or the privacy notice, we will let you know calmly inside the app first — never by surprise email.",

    // Discover tab (Phase 1)
    discoverTitle: "Discover",
    discoverBackHome: "Back home",
    discoverPickWay: "Choose gently",
    discoverUseCamera: "Use camera",
    discoverUploadImage: "Upload a photo",
    discoverHelp: "Point softly at one object. Hold still. Then tap the shutter.",
    discoverShutter: "Take photo",
    discoverRetake: "Retake softly",
    discoverLooking: "Looking gently...",
    discoverNothing: "I couldn't quite recognize that yet. Let's try another discovery.",
    discoverObject: "I see",
    discoverEnglish: "English",
    discoverSpanish: "Español",
    discoverFrench: "Français",
    discoverListen: "Listen",
    discoverSpeaking: "Speaking softly...",
    discoverSaveBee: "Save to my hive",
    discoverSaved: "Tucked into your hive ✿",
    discoverMyHive: "My discoveries",
    discoverHiveEmpty: "Your discovery hive is waiting. Find your first calm object.",
    discoverUnsafe: "Let's find a soft, calm object together.",
    discoverPermissionTitle: "Camera is shy",
    discoverPermissionBody: "Allow camera in your browser, or upload a photo instead.",
    discoverTileLabel: "Discover",
    discoverSafetyTitle: "A gentle reminder",
    discoverSafetyBody: "Point the camera toward objects, nature, learning materials, or safe discoveries. Faces and selfies aren't part of the discovery game.",
    discoverPrivacyNote: "Photos are processed briefly and never stored as images on our servers.",
    landTagSafe: "safe learning",
    landTagSafeBefore: "A peaceful little hive for ",
    landTagSafeAfter: " and emotional growth.",
    landSubhead: "Sensory-friendly. No flashy rewards. No noisy distractions. Just gentle activities, slow stories, and calming breath — all built for your little one.",
    landTrustChip: "COPPA-conscious • No ads • No dopamine loops",
    landBreatheSoftly: "Breathe softly",
    landBreathePace: "4 sec in · 6 sec out",
    landPillarEmotionalTitle: "Emotional Safety",
    landPillarEmotionalBody: "Feelings check-ins, kindness lessons, and self-regulation tools woven gently into daily play.",
    landPillarBedtimeTitle: "Bedtime Mode",
    landPillarBedtimeBody: "A dimmed honey-night palette, AI-woven bedtime stories, lullaby ambience, and slow breathing.",
    landPillarGardenTitle: "Growing Garden",
    landPillarGardenBody: "Progress blooms as flowers and a hive — never flashing badges or addictive streaks.",
    landPillarSensoryTitle: "Sensory-Friendly",
    landPillarSensoryBody: "Reduce motion, lower sound, increase contrast, switch to dyslexia-friendly font — all in one tap.",
    landPillarTrustTitle: "Parent Trust",
    landPillarTrustBody: "Screen-time controls, wellness insights, bedtime tracking, and emotional check-in history.",
    landPillarSlowTitle: "Slow by Design",
    landPillarSlowBody: "One task at a time. Predictable, unhurried navigation. Built for nervous-system regulation.",
    landBottomTitle: "Ready to settle into the hive?",
    landBottomBody: "Create a parent account, add your little ones, and customize their sensory experience. Free to start.",
    landHeroAlt: "Child playing calmly with wooden toys",

    // Onboarding
    onbStepProgress: "Step",
    onbOf: "of",
    onbBack: "Back",
    onbNext: "Next",
    onbFinish: "Finish gently",
    onbReady: "Your nest is ready ✿",
    onbSaveFailed: "Could not save just yet",
    onbWelcomeTag: "For you",
    onbWelcomeTitle: "Welcome, {name} ✿",
    onbWelcomeBody1: "Your parent account is settled in the hive. Thank you for joining us softly.",
    onbWelcomeBody2: "The next few gentle questions are about your child — so BeeNest can shape itself with care around their pace, their senses, and their world.",
    onbWelcomeBody3: "You can pause anytime. There are no wrong answers.",
    onbWelcomeBegin: "Begin gently",
    onbAboutChildTag: "About your child",
    onbQ_child_age: "What's your child's age range?",
    onbQ_learning_needs: "What kind of learning would you like to nurture?",
    onbQ_sensory_sensitivity: "How sensitive is your child to stimulation?",
    onbQ_goals: "What are your gentle goals?",
    onbQ_language: "Preferred language?",
    onbQ_bedtime_challenges: "Any bedtime challenges? (optional)",
    onbQ_emotional_support_needs: "What emotional support feels most needed?",
    onbOpt_Letters: "Letters & reading",
    onbOpt_Numbers: "Numbers & shapes",
    onbOpt_EmotionalLearning: "Emotional learning",
    onbOpt_Mindfulness: "Mindfulness",
    onbOpt_Creativity: "Creativity",
    onbOpt_Nature: "Nature",
    onbOpt_VerySensitive: "Very sensitive",
    onbOpt_SomewhatSensitive: "Somewhat sensitive",
    onbOpt_Average: "Average",
    onbOpt_CalmerRoutines: "Calmer routines",
    onbOpt_BetterSleep: "Better sleep",
    onbOpt_EmotionalRegulation: "Emotional regulation",
    onbOpt_Confidence: "Confidence",
    onbOpt_Focus: "Focus",
    onbOpt_Joy: "Joy",
    onbOpt_HardToWindDown: "Hard to wind down",
    onbOpt_AnxiousAtNight: "Anxious at night",
    onbOpt_RestlessSleep: "Restless sleep",
    onbOpt_None: "None",
    onbOpt_BigFeelings: "Big feelings",
    onbOpt_Worry: "Worry",
    onbOpt_Frustration: "Frustration",
    onbOpt_Shyness: "Shyness",
    onbOpt_AllOfTheAbove: "All of the above",

    // ChildHome
    childGreetingTag: "Hello, little bee",
    childHi: "Hi",
    childWindDownTag: "Wind down",
    childBedtimeHere: "Bedtime is here",
    childBedtimeIn: "Bedtime in",
    childBedtimeMin: "min",
    childRestUntil: "Rest gently until",
    childTapQuietHive: "Tap to enter the quiet hive",
    childTodayFocus: "Today's gentle focus",
    childAllActivities: "All gentle activities",
    childTileBedtime: "Bedtime",
    childTileFeelings: "Feelings",
    childTileSounds: "Calm sounds",
    childTileGarden: "Garden",

    // LearningCategory
    learnBackHome: "Back home",
    learnStoryIntro: "Tap below and we'll weave a gentle story just for you.",
    learnWeaving: "Weaving...",
    learnWeaveStory: "Weave a story",
    learnNewStory: "New story",
    learnNextItem: "Next gently",
    learnTuningVoice: "Tuning voice...",
    learnListenSoftly: "Listen softly",
    learnPause: "Pause",
    learnPlay: "Play",
    learnStoryResting: "Story is resting, try again softly",
    learnVoiceResting: "Voice is resting",
    learnDefaultMoment: "A gentle moment together",

    // Bedtime
    bedBackHome: "Back home",
    bedTitle: "Time to rest, little bee",
    bedSubtitle: "The hive is quiet. The moon is humming softly.",
    bedIntro: "Tap below to weave a bedtime story just for tonight.",
    bedWeaving: "Weaving...",
    bedWeaveBedtime: "Weave a bedtime story",
    bedAnother: "Another",
    bedNarrating: "Tuning...",
    bedShareGift: "Share a Quiet Moment",
    bedWrapping: "Wrapping...",
    bedGiftReady: "A gentle gift link is ready ✿",
    bedGiftBody: "Share with grandparents or family — no sign-up needed for them to read or hear it.",
    bedCopyLink: "Copy link",
    bedCopied: "Copied",
    bedLinkCopied: "Link copied softly ✿",
    bedCouldNotWeave: "Could not weave just yet",
    bedCouldNotGift: "Could not weave a gift just yet",

    // Emotions
    emoBackHome: "Back home",
    emoTitle: "How are you feeling?",
    emoSubtitle: "Every feeling is welcome here.",
    emoSavedThanks: "Thank you for sharing ✿",
    emoSaveFailed: "Could not save",
    emoSave: "Save gently",
    emoCalmingBreath: "A calming breath",
    emoBreatheBody: "Watch the bee circle and breathe with it.",
    emoBreathIn: "Breathe in...",
    emoBreathOut: "Breathe out softly...",
    emoTapBegin: "Tap to begin",
    emoPauseGently: "Pause gently",
    emoBeginBreath: "Begin breathing",

    // Garden
    gardenBackHome: "Back home",
    gardenTitle: "Your Garden",
    gardenSubtitle: "Every gentle activity grows a flower.",
    gardenFlower: "flower",
    gardenFlowers: "flowers",
    gardenWaiting: "Your garden is waiting. Try a gentle activity to grow your first flower.",
    gardenByCategory: "By category",
    gardenWillGather: "Progress will gather here as you play.",
    gardenTimes: "times",
    gardenTotalMinutes: "Total minutes:",
    gardenCat_letters: "Letters",
    gardenCat_numbers: "Numbers",
    gardenCat_shapes: "Shapes",
    gardenCat_colors: "Colors",
    gardenCat_emotions: "Feelings",
    gardenCat_mindfulness: "Mindfulness",
    gardenCat_creativity: "Creativity",
    gardenCat_nature: "Nature",
    gardenCat_music: "Music",
    gardenCat_stories: "Stories",
    gardenCat_focus: "Focus",
    gardenCat_kindness: "Kindness",
    gardenCat_bedtime: "Bedtime",

    // Membership
    memTitle: "Tend the hive together",
    memSubtitle: "BeeNest is built without ads or addictive loops. Your support keeps it gentle.",
    memActiveBadge: "Active member ✿",
    memMonthly: "Monthly",
    memYearly: "Yearly",
    memPerMonth: "/ month",
    memPerYear: "/ year",
    memSavePct: "Save 25%",
    memChoose: "Choose",
    memOpening: "Opening checkout...",
    memCheckoutFailed: "Checkout could not open",
    memPerk1: "Unlimited AI bedtime stories",
    memPerk2: "All sensory tools",
    memPerk3: "Multi-child profiles",
    memPerk4: "Wellness insights",
    memPerkY1: "Everything in Monthly",
    memPerkY2: "Priority support",
    memPerkY3: "Printable activity sheets",
    memPerkY4: "Early access to new tools",

    // MembershipSuccess
    msChecking: "Confirming your gentle support...",
    msCheckingBody: "This will only take a moment.",
    msPaid: "Welcome to the full hive ✿",
    msPaidBody: "Your membership is active. Thank you for tending this nest.",
    msBackToNest: "Back to your nest",
    msExpired: "Session rested too long",
    msTryAgain: "Try again gently",
    msQuietMoment: "A quiet moment",
    msQuietBody: "We'll keep checking. You can return to your nest anytime.",
    msBackDashboard: "Back to dashboard",

    // GiftStory
    giftLabel: "A Quiet Moment ✿",
    giftFrom: "From",
    giftTo: "to",
    giftPause: "Pause narration",
    giftListen: "Listen softly",
    giftNotFound: "This quiet moment cannot be found.",
    giftVisit: "Visit BeeNest",
    giftFooterBody: "This story was woven on BeeNest — a calm, sensory-friendly hive of learning and wellness for little ones.",
    giftDiscover: "Discover BeeNest",

    // LetterDetail
    ldAllLetters: "All letters",
    ldPrint: "Print / Save PDF",
    ldFamilyReflection: "BeeNest · Family Reflection",
    ldForHive: "For your gentle hive",
    ldFooter: "Every gentle moment matters · BeeNest is honored to be part of your family's journey.",
    ldNoteTitle: "A small note from your family",
    ldNoteIntro: "Tuck a private note into this letter — a memory, a small joy, a small worry. It stays only with you.",
    ldNotePlaceholder: "A tender memory or thought...",
    ldNoteSave: "Save note",
    ldNoteSaving: "Saving...",
    ldNoteSaved: "Note tucked into the letter ✿",
    ldNoteSaveFailed: "Couldn't save just yet",

    // Settings
    setSubtitle: "Customize the hive for your family's senses.",
    setSensory: "Sensory",
    setLanguageSection: "Language",
    setAppLanguage: "App language",
    setBedtimeSection: "Bedtime mode",
    setBedtimeDimmed: "Dimmed honey-night palette",
    setBedtimeDesc: "Softer indigo background, warmer text.",
    setRemSection: "Bedtime reminders",
    setRemIntro: "Optional and always gentle. A soft invitation, never a demand. Off by default.",
    setRemEnable: "Enable gentle reminders",
    setRemEnableDesc: "A single soft banner around bedtime. Easy to turn off anytime.",
    setRemTime: "Reminder time",
    setRemQuietDays: "Quiet days (no reminder)",
    setRemBrowserTitle: "Browser notification too",
    setRemBrowserDesc: "Optional. A single silent system notification when the tab is open.",
    setRemBrowserDenied: "Browser permission was not granted — keeping reminders in-app only.",
    setRemPreviewLabel: "Preview:",
    setRemPreviewQuote: "Your hive may be ready for a gentle reset.",
    setRemPreviewMeta: "No urgency, no streaks, no guilt. Reminders are a soft invitation only.",
    setPauseSection: "Gentle Reflection Pause (for you)",
    setPauseIntro: "BeeNest also nurtures caregivers. About once a week, a soft optional invite may appear on your dashboard. You can begin one anytime, or turn this off entirely.",
    setPauseEnable: "Enable Reflection Pause invites",
    setPauseEnableDesc: "Soft weekly invitation — never urgent, always dismissable.",
    setPauseBeginNow: "Begin a pause now",
    setMon: "mon", setTue: "tue", setWed: "wed", setThu: "thu", setFri: "fri", setSat: "sat", setSun: "sun",
  },

  es: {
    // Branding
    appName: "BeeNest",
    tagline: "Una pequeña colmena tranquila para aprender con seguridad.",

    // Auth
    getStarted: "Empieza con calma",
    signIn: "Iniciar sesión",
    createAccount: "Crear cuenta",
    email: "Correo",
    password: "Contraseña",
    name: "Tu nombre",
    welcome: "Bienvenido",
    welcomeChild: "Hola, pequeña abeja",
    alreadyInHive: "¿Ya estás en la colmena? ",
    newHere: "¿Nuevo aquí? ",
    softWelcome: "Un nido suave para tu familia.",
    softReturn: "Vuelve a entrar con calma.",
    forgotPasswordLink: "¿Olvidaste tu contraseña?",
    forgotTitle: "Un camino suave para reiniciar",
    forgotBody: "Comparte tu correo y prepararemos en silencio un enlace para reiniciar.",
    forgotSubmit: "Enviar el enlace suave",
    forgotSending: "Preparando...",
    forgotSuccess: "Si ese correo está en nuestra colmena, hemos preparado un camino suave de reinicio.",
    forgotBackToSignIn: "Volver a iniciar sesión",
    resetTitle: "Elige una nueva contraseña",
    resetBody: "Elige algo fácil de recordar. Al menos 8 caracteres suaves.",
    resetNewPassword: "Nueva contraseña",
    resetSubmit: "Acomodar",
    resetSubmitting: "Acomodando...",
    resetSuccess: "Tu contraseña se acomodó con suavidad. Inicia sesión cuando estés lista/o.",
    resetInvalid: "Este enlace de reinicio ha descansado. Por favor pide uno nuevo.",
    resetGoSignIn: "Ir a iniciar sesión",

    // Dashboard / Nav
    parentDashboard: "Nido de Padres",
    childProfiles: "Perfiles infantiles",
    addChild: "Añadir niño/a",
    todaysFocus: "El enfoque suave de hoy",
    settings: "Ajustes",
    membership: "Membresía",
    familyLetters: "Cartas de Reflexión Familiar",
    logout: "Salir",
    chooseChild: "Elige un perfil",
    progress: "Progreso",
    insights: "Bienestar",
    day: "Día",
    night: "Noche",
    toggleBedtime: "Cambiar modo noche",
    languageMenu: "Idioma",

    // Dashboard page
    dashChildren: "Niños/as",
    dashActivities: "Actividades",
    dashMinutes: "Minutos",
    dashFlowers: "Flores",
    dashRecentCheckins: "Sentimientos recientes",
    dashNoCheckins: "Aún no hay registros — los sentimientos se reunirán aquí.",
    dashNoChildren: "Aún no hay pequeños. Añade un perfil para comenzar con calma.",
    dashAgesPrefix: "Edades",
    dashFlowersSuffix: "flores",
    dashRhythmsLabel: "Ritmos de la Colmena",
    dashRhythmsCount: "momentos de calma cuidados esta semana",
    dashRhythmsOpen: "abre la reflexión suave →",
    dashSensoryTitle: "Ajustes Sensoriales y de Sueño",
    dashSensoryBody: "Ajusta sonido, movimiento, contraste y modo noche.",
    dashLettersTitle: "Cartas de Reflexión Familiar",
    dashLettersBody: "Una nota mensual suave de BeeNest sobre tus ritmos tranquilos.",
    dashMembershipBody: "Apoya a BeeNest y abre la colmena completa.",

    // Pause invite (dashboard)
    pauseInviteTag: "Para ti",
    pauseInviteBody: "Tu colmena ha estado reuniendo momentos silenciosos. ¿Te gustaría una pequeña pausa para reflexionar?",
    pauseInviteBegin: "Comenzar con calma",
    pauseInviteLater: "Quizá más tarde",

    // Profiles page
    profilesTitle: "Perfiles infantiles",
    profilesSubtitle: "Cada perfil lleva su propio ritmo suave.",
    profilesAddAnother: "Añadir otra abejita",
    profilesNewBee: "Una nueva abejita",
    profilesNameLabel: "Nombre",
    profilesAgeLabel: "Rango de edad",
    profilesInterestsLabel: "Intereses (separados por coma)",
    profilesInterestsPlaceholder: "abejas, cuentos, pintura",
    profilesLanguageLabel: "Idioma",
    profilesCancel: "Cancelar",
    profilesSave: "Guardar con calma",
    profilesSaving: "Guardando...",
    profilesNewBeeAdded: "Nueva abejita añadida ✿",
    profilesSaveFailed: "No se pudo guardar el perfil",
    profilesRemoved: "Perfil retirado con suavidad",
    profilesEditBedtime: "Editar rutina de sueño",
    profilesEditSensory: "Editar perfil sensorial",
    profilesRemove: "Quitar perfil",
    bedtimeDialogTitle: "Rutina de sueño",
    bedtimeDialogIntro: "Sugeriremos suavemente la colmena del sueño 30 minutos antes y durante la ventana.",
    bedtimeEnableLabel: "Activar rutina de sueño",
    bedtimeEnableHint: "Aviso suave en la pantalla de inicio",
    bedtimeStartTime: "Hora de inicio",
    bedtimeEndTime: "Hora de fin",
    bedtimeSaved: "Rutina de sueño guardada ✿",
    bedtimeSaveFailed: "Aún no pudimos guardar",
    sensoryDialogTitle: "Perfil sensorial",
    sensoryDialogIntro: "Cada niño/a puede tener sus propias sensibilidades. \"Heredar\" usa los ajustes del padre.",
    sensorySaved: "Perfil sensorial guardado ✿",
    sensorySound: "Sonido",
    sensoryMotion: "Animación",
    sensoryContrast: "Contraste",
    sensoryFont: "Fuente",
    sensoryInherit: "Heredar",
    sensoryMute: "Silencio",
    sensoryLow: "Bajo",
    sensoryNormal: "Normal",
    sensoryReduced: "Reducida",
    sensoryFull: "Completa",
    sensoryStandard: "Estándar",
    sensoryHigh: "Alto",
    sensoryDyslexia: "Para dislexia",

    // HiveReset page
    hiveResetTag: "Reinicio de la Colmena",
    hiveResetExit: "Salir del ritual",
    hiveResetBreathIn: "Inhala despacio...",
    hiveResetHold: "Sostén con suavidad...",
    hiveResetBreathOut: "Exhala suavemente...",
    hiveResetBreathHelp: "Dos ciclos lentos · luego un cuento suave",
    hiveResetSkipBreath: "Ir al cuento",
    hiveResetWeaving: "Tejiendo un cuento tranquilo para esta noche...",
    hiveResetStoryResting: "El cuento descansa esta noche.",
    hiveResetGoBedtime: "Ir a Hora de dormir",
    hiveResetSleepTight: "Duerme dulce ✿",
    hiveResetSleepTightHeading: "Duerme dulce, pequeña abeja",
    hiveResetHiveQuiet: "La colmena está en silencio.",
    hiveResetBackHome: "Volver al inicio",
    hiveResetAnother: "Otro cuento tranquilo",

    // Rhythms page
    rhythmsBack: "Volver al nido",
    rhythmsLabel: "Ritmos de la Colmena",
    rhythmsHeading: "Momentos de Calma",
    rhythmsSoftMoment: "momento suave",
    rhythmsSoftMoments: "momentos suaves",
    rhythmsBedtimeTitle: "Cuentos de buenas noches compartidos",
    rhythmsBedtimeBody: "esta semana — cada cuento descansa en tu colmena.",
    rhythmsFeelingsTitle: "Sentimientos compartidos",
    rhythmsFeelingsEmpty: "Cuando esté listo, los sentimientos se reunirán aquí con suavidad.",
    rhythmsCheckins: "registros · más a menudo",
    rhythmsMomentsTitle: "Momentos de calma",
    rhythmsMomentsBody: "momentos suaves cuidados esta semana.",
    rhythmsDaysTitle: "Días con calma",
    rhythmsDaysBody: "días de esta semana tuvieron una pausa tranquila. El descanso también es parte de tu ritmo.",
    rhythmsFavoritesTitle: "Tus espacios de calma favoritos",
    rhythmsFavoritesEmpty: "Cuando tu pequeño descanse en cualquier espacio de calma, aparecerá aquí con suavidad.",
    rhythmsFooter: "El progreso es suave y no lineal. Tu colmena siempre está aquí — sin presión, sin puntuación.",
    rhythmsMoment: "momento",
    rhythmsMoments: "momentos",
    feelJoyful: "alegre",
    feelCalmAdj: "tranquilo",
    feelRested: "descansado",
    feelTender: "tierno",
    feelHonest: "sincero",
    feelLively: "vivo",

    // Letters page
    lettersBack: "Volver al nido",
    lettersTag: "Cartas de Reflexión Familiar",
    lettersHeading: "Notas amables desde la colmena",
    lettersSubtitle: "Una carta mensual suave por cada niño/a — la reflexión de un amigo cuidadoso sobre tus ritmos tranquilos.",
    lettersComposeMonth: "Componer la carta de este mes",
    lettersCompose: "Componer",
    lettersComposing: "Componiendo...",
    lettersReady: "Carta lista ✿",
    lettersComposed: "Una carta suave ha sido compuesta ✿",
    lettersResting: "La carta descansa esta noche. Inténtalo de nuevo con calma.",
    lettersEmpty: "Aún no hay cartas. Compón la primera arriba cuando estés lista/o.",

    // Pause page
    pauseReturn: "Volver con calma",
    pauseLabel: "Pausa de Reflexión Suave",
    pauseWelcomeHeading: "Una respiración tranquila, sólo para ti",
    pauseWelcomeBody: "Has estado cuidando tu colmena. Este momento también es para ti. Tómate 60–90 segundos suaves — vuelve cuando quieras.",
    pauseLater: "Quizá más tarde",
    pauseBegin: "Comenzar con calma",
    pauseSettleIn: "Acomódate con suavidad...",
    pauseBreathIn: "Inhala despacio...",
    pauseHold: "Sostén con suavidad...",
    pauseBreathOut: "Exhala suavemente...",
    pauseBreathHelper: "Suaves abejas zumbando · luna gentil",
    pauseGathering: "Reuniendo un pensamiento tranquilo para ti...",
    pauseFallback: "Lo que sea que haya traído este día, te has presentado con suavidad. El descanso también es parte del cuidado.\n\nTu colmena se beneficia de tu ternura también.",
    pauseContinue: "Continuar",
    pauseCloseHeading: "Tú también mereces suavidad",
    pauseCloseBody: "La colmena es suave, y tú también.",

    // Calm Sounds page
    soundsBack: "Volver al inicio",
    soundsTitle: "Sonidos de calma",
    soundsSubtitle: "Elige una atmósfera suave. Toca otra vez para descansar el sonido.",
    soundsMutedTitle: "Los sonidos están descansando en esta colmena.",
    soundsMutedBody: "El nivel de sonido sensorial está en Silencio. Ajústalo en Ajustes o en el perfil sensorial de este niño.",
    soundsVolumeLabel: "Volumen",
    soundsVolumeCapped: "· limitado (sensorial bajo)",
    soundsFooter: "Todos los sonidos los teje suavemente la app — nunca realistas, nunca agudos.",
    soundRain: "Lluvia suave",
    soundForest: "Susurro del bosque",
    soundBees: "Abejas zumbando",
    soundWind: "Viento gentil",
    soundOcean: "Aliento del océano",

    // Child experience
    bedtime: "Hora de dormir",
    feelings: "Sentimientos",
    breathing: "Respirar",
    breathHint: "Inhala despacio... y exhala suavemente",
    howFeel: "¿Cómo te sientes, pequeñín?",
    feelHappy: "Feliz",
    feelCalm: "Tranquilo",
    feelTired: "Cansado",
    feelSad: "Triste",
    feelMad: "Frustrado",
    feelExcited: "Emocionado",
    save: "Guardar",
    listen: "Escuchar",
    newStory: "Tejer un cuento",
    backHome: "Volver",
    minutes: "minutos",
    flowers: "flores",
    activities: "Actividades",
    garden: "Tu jardín",

    // Settings labels
    soundLevel: "Volumen",
    motion: "Animación",
    contrast: "Contraste",
    font: "Fuente",
    bedtimeMode: "Modo noche",
    language: "Idioma",

    // Soft system messages
    loadingSoftly: "Acomodándonos con calma...",
    softError: "Descansa un momento e inténtalo de nuevo.",
    somethingGentlyWrong: "Algo sucedió suavemente. Intenta de nuevo.",
    voiceResting: "La voz está descansando. Inténtalo en un momento.",
    storyResting: "El cuento está descansando. Por favor, intenta de nuevo con calma.",
    letterResting: "La carta descansa esta noche. Por favor, intenta de nuevo con calma.",
    welcomeToHive: "Bienvenido a la colmena ✿",
    welcomeBackSoft: "Bienvenido de vuelta ✿",
    sessionRested: "Tu sesión necesitó un descanso. Vuelve a iniciar sesión, por favor.",

    // Footer
    footer: "BeeNest™ by IM•BEE — hecho con cuidado, para cada corazoncito.",
    footerAttribution: "concepto, mascota y sistema educativo creados por Evelyn Villanueva. Todos los derechos reservados.",
    footerPrivacy: "Privacidad",
    footerTerms: "Términos",

    // Auth surface polish
    authRegisterSub: "Un nido tranquilo para tu familia.",
    authLoginSub: "Vuelve con calma.",
    authWelcomeIn: "Bienvenida/o a la colmena ✿",
    authWelcomeBack: "Qué bueno verte de nuevo ✿",
    authSoftError: "Algo se tropezó suavemente. Intenta de nuevo, con calma.",
    passwordShow: "Mostrar contraseña",
    passwordHide: "Ocultar contraseña",
    forgotDevModeLabel: "Enlace de reseteo (modo de pruebas — aún sin correo)",

    // Onboarding / Profiles warmth
    profilesAddFirstBee: "Añade a tu primera abejita",

    // Discover safety + moderation
    discoverFaceGently: "Las caras y las selfies no forman parte de la colmena de descubrimiento. Mejor apunta a un objeto, una plantita o algo tranquilo de la habitación.",
    discoverUnsafeGently: "Mejor elijamos algo más suavecito para descubrir — una hoja, un juguete, una fruta o algo amable del cuarto.",
    discoverNoCamera: "Este navegador es tímido con la cámara. Sube una foto y la descubrimos juntos.",

    // Privacy page
    privacyTitle: "La privacidad, con calma",
    privacyIntro: "BeeNest fue creado para familias cansadas y corazoncitos sensibles. Así cuidamos tu información — bajito, con cariño.",
    privacyH1: "Lo que guardamos",
    privacyP1: "Solo lo necesario: tu correo y una contraseña cifrada para acompañarte al entrar, el nombre y la edad aproximada de tu pequeño/a para acomodar la experiencia con cuidado, y las actividades suaves que eliges dentro de la colmena. Sin píxeles de seguimiento. Sin redes de anuncios. Sin complementos sociales.",
    privacyH2: "Lo que no guardamos",
    privacyP2: "No recogemos el nombre completo de tu pequeño/a, ni fotos, ni grabaciones de voz, ni ubicación, ni contactos, ni identificadores del dispositivo. Las fotos que se usan en Descubrir se procesan brevemente y nunca se almacenan como imágenes en nuestros servidores — solo se guarda la frase suave si tú eliges conservarla.",
    privacyH3: "La IA y tus datos",
    privacyP3: "Cuando usas una función creativa (un cuento de buenas noches, una carta de reflexión, un descubrimiento), enviamos el contexto necesario de forma segura a un modelo para componer la respuesta. Los textos se limpian de información personal. No permitimos que tus datos se usen para entrenar modelos de terceros.",
    privacyH4: "La seguridad de la infancia",
    privacyP4: "BeeNest respeta los principios de COPPA. Las cuentas se crean por la persona cuidadora, no por el niño o la niña. Los perfiles de los pequeños no tienen inicio de sesión propio, ni contraseña, ni datos de contacto. Toda la configuración la maneja quien cuida.",
    privacyH5: "Cómo encontrarnos",
    privacyP5: "Para dudas, eliminar tu cuenta o cualquier otra cosa — escríbenos desde el canal de soporte dentro de Ajustes. Respondemos con palabras claras, nunca con tecnicismos.",

    // Terms page
    termsTitle: "Un acuerdo suave",
    termsIntro: "Estas son las pequeñas promesas que hacemos juntos cuando usas BeeNest. No son tecnicismos — son un acuerdo tranquilo entre tu familia y nosotros.",
    termsH1: "Qué es BeeNest",
    termsP1: "BeeNest es un compañero tranquilo de aprendizaje y bienestar para personas cuidadoras y niños y niñas de 3 a 10 años, pensado con sensibilidad sensorial. No es un servicio médico, ni una herramienta de diagnóstico, ni un sustituto del cuidado profesional.",
    termsH2: "Quién puede usarlo",
    termsP2: "Las cuentas las crea una madre, padre o persona cuidadora mayor de 18 años. Los pequeños viven la experiencia con su acompañamiento y permiso.",
    termsH3: "Cómo cuidamos la calma",
    termsP3: "No mostramos anuncios. No gamificamos la atención. No usamos rachas, tablas de clasificación ni notificaciones diseñadas para atraerte de vuelta. Si algo se siente demasiado estimulante, puedes pausar o cerrarlo en cualquier momento.",
    termsH4: "Tu contenido",
    termsP4: "El progreso, las emociones, los descubrimientos y las cartas guardadas de tu pequeño/a pertenecen a tu familia. Puedes eliminarlos cuando quieras desde Ajustes. Nunca venderemos ni compartiremos ese contenido.",
    termsH5: "Si algo cambia",
    termsP5: "Si alguna vez necesitamos actualizar estos términos o la privacidad, te avisaremos con calma dentro de la app primero — nunca por sorpresa en el correo.",

    // Discover tab (Phase 1)
    discoverTitle: "Descubrir",
    discoverBackHome: "Volver al inicio",
    discoverPickWay: "Elige con calma",
    discoverUseCamera: "Usar la cámara",
    discoverUploadImage: "Subir una foto",
    discoverHelp: "Apunta con suavidad a un objeto. Mantén la cámara quieta. Luego toca el botón.",
    discoverShutter: "Tomar foto",
    discoverRetake: "Volver a intentar",
    discoverLooking: "Mirando con calma...",
    discoverNothing: "Aún no pude reconocer eso. Intentemos otro descubrimiento.",
    discoverObject: "Veo",
    discoverEnglish: "English",
    discoverSpanish: "Español",
    discoverFrench: "Français",
    discoverListen: "Escuchar",
    discoverSpeaking: "Hablando con calma...",
    discoverSaveBee: "Guardar en mi colmena",
    discoverSaved: "Guardado en tu colmena ✿",
    discoverMyHive: "Mis descubrimientos",
    discoverHiveEmpty: "Tu colmena de descubrimientos espera. Encuentra tu primer objeto tranquilo.",
    discoverUnsafe: "Busquemos juntos un objeto suave y tranquilo.",
    discoverPermissionTitle: "La cámara es tímida",
    discoverPermissionBody: "Permite la cámara en tu navegador, o sube una foto.",
    discoverTileLabel: "Descubrir",
    discoverSafetyTitle: "Un recordatorio suave",
    discoverSafetyBody: "Apunta la cámara hacia objetos, naturaleza, materiales de aprendizaje o descubrimientos seguros. Las caras y las selfies no forman parte del juego de descubrir.",
    discoverPrivacyNote: "Las fotos se procesan brevemente y nunca se guardan como imágenes en nuestros servidores.",
    landTagSafe: "aprender con seguridad",
    landTagSafeBefore: "Una colmena tranquila para ",
    landTagSafeAfter: " y crecer emocionalmente.",
    landSubhead: "Pensada para los sentidos. Sin recompensas brillantes. Sin distracciones ruidosas. Solo actividades suaves, cuentos lentos y respiraciones tranquilas — todo para tu pequeño/a.",
    landTrustChip: "Respeta COPPA • Sin anuncios • Sin bucles adictivos",
    landBreatheSoftly: "Respira con suavidad",
    landBreathePace: "4 seg al inhalar · 6 seg al exhalar",
    landPillarEmotionalTitle: "Seguridad emocional",
    landPillarEmotionalBody: "Registros de sentimientos, lecciones de bondad y herramientas de autorregulación tejidas con suavidad en el juego diario.",
    landPillarBedtimeTitle: "Modo noche",
    landPillarBedtimeBody: "Una paleta tenue color miel, cuentos de buenas noches tejidos con IA, ambiente de nana y respiración lenta.",
    landPillarGardenTitle: "Jardín que crece",
    landPillarGardenBody: "El progreso florece como flores y una colmena — nunca insignias destellantes ni rachas adictivas.",
    landPillarSensoryTitle: "Amigable con los sentidos",
    landPillarSensoryBody: "Reduce el movimiento, baja el sonido, sube el contraste, cambia a una fuente para dislexia — todo con un toque.",
    landPillarTrustTitle: "Confianza para padres",
    landPillarTrustBody: "Controles de tiempo en pantalla, miradas al bienestar, seguimiento del sueño e historial de sentimientos.",
    landPillarSlowTitle: "Lento por diseño",
    landPillarSlowBody: "Una tarea a la vez. Navegación predecible y sin prisa. Pensada para regular el sistema nervioso.",
    landBottomTitle: "¿Listo/a para acomodarte en la colmena?",
    landBottomBody: "Crea una cuenta de padre o madre, añade a tus pequeños y personaliza su experiencia sensorial. Gratis para empezar.",
    landHeroAlt: "Niño/a jugando con calma con juguetes de madera",

    // Onboarding
    onbStepProgress: "Paso",
    onbOf: "de",
    onbBack: "Atrás",
    onbNext: "Siguiente",
    onbFinish: "Terminar con calma",
    onbReady: "Tu nido está listo ✿",
    onbSaveFailed: "Aún no pudimos guardar",
    onbWelcomeTag: "Para ti",
    onbWelcomeTitle: "Bienvenida/o, {name} ✿",
    onbWelcomeBody1: "Tu cuenta de padre o madre ya está acomodada en la colmena. Gracias por unirte con calma.",
    onbWelcomeBody2: "Las siguientes preguntas suaves son sobre tu pequeño/a — para que BeeNest se acomode con cuidado a su ritmo, sus sentidos y su mundo.",
    onbWelcomeBody3: "Puedes pausar cuando quieras. No hay respuestas incorrectas.",
    onbWelcomeBegin: "Comenzar con calma",
    onbAboutChildTag: "Sobre tu pequeño/a",
    onbQ_child_age: "¿Cuál es el rango de edad de tu pequeño/a?",
    onbQ_learning_needs: "¿Qué tipo de aprendizaje te gustaría nutrir?",
    onbQ_sensory_sensitivity: "¿Qué tan sensible es tu pequeño/a a la estimulación?",
    onbQ_goals: "¿Cuáles son tus metas suaves?",
    onbQ_language: "¿Idioma preferido?",
    onbQ_bedtime_challenges: "¿Algún reto a la hora de dormir? (opcional)",
    onbQ_emotional_support_needs: "¿Qué apoyo emocional sientes más necesario?",
    onbOpt_Letters: "Letras y lectura",
    onbOpt_Numbers: "Números y formas",
    onbOpt_EmotionalLearning: "Aprendizaje emocional",
    onbOpt_Mindfulness: "Atención plena",
    onbOpt_Creativity: "Creatividad",
    onbOpt_Nature: "Naturaleza",
    onbOpt_VerySensitive: "Muy sensible",
    onbOpt_SomewhatSensitive: "Algo sensible",
    onbOpt_Average: "Promedio",
    onbOpt_CalmerRoutines: "Rutinas más tranquilas",
    onbOpt_BetterSleep: "Mejor sueño",
    onbOpt_EmotionalRegulation: "Regulación emocional",
    onbOpt_Confidence: "Confianza",
    onbOpt_Focus: "Atención",
    onbOpt_Joy: "Alegría",
    onbOpt_HardToWindDown: "Le cuesta calmarse",
    onbOpt_AnxiousAtNight: "Ansioso/a por la noche",
    onbOpt_RestlessSleep: "Sueño inquieto",
    onbOpt_None: "Ninguno",
    onbOpt_BigFeelings: "Sentimientos grandes",
    onbOpt_Worry: "Preocupación",
    onbOpt_Frustration: "Frustración",
    onbOpt_Shyness: "Timidez",
    onbOpt_AllOfTheAbove: "Todo lo anterior",

    // ChildHome
    childGreetingTag: "Hola, pequeña abeja",
    childHi: "Hola",
    childWindDownTag: "Calma antes de dormir",
    childBedtimeHere: "Es hora de dormir",
    childBedtimeIn: "Dormir en",
    childBedtimeMin: "min",
    childRestUntil: "Descansa con calma hasta",
    childTapQuietHive: "Toca para entrar a la colmena tranquila",
    childTodayFocus: "El enfoque suave de hoy",
    childAllActivities: "Todas las actividades suaves",
    childTileBedtime: "Hora de dormir",
    childTileFeelings: "Sentimientos",
    childTileSounds: "Sonidos de calma",
    childTileGarden: "Jardín",

    // LearningCategory
    learnBackHome: "Volver al inicio",
    learnStoryIntro: "Toca abajo y tejeremos un cuento suave solo para ti.",
    learnWeaving: "Tejiendo...",
    learnWeaveStory: "Tejer un cuento",
    learnNewStory: "Nuevo cuento",
    learnNextItem: "Siguiente con calma",
    learnTuningVoice: "Afinando la voz...",
    learnListenSoftly: "Escuchar con calma",
    learnPause: "Pausa",
    learnPlay: "Reproducir",
    learnStoryResting: "El cuento descansa, intenta de nuevo con calma",
    learnVoiceResting: "La voz está descansando",
    learnDefaultMoment: "Un momento suave juntos",

    // Bedtime
    bedBackHome: "Volver al inicio",
    bedTitle: "Es hora de descansar, pequeña abeja",
    bedSubtitle: "La colmena está tranquila. La luna canturrea con suavidad.",
    bedIntro: "Toca abajo para tejer un cuento de buenas noches solo para esta noche.",
    bedWeaving: "Tejiendo...",
    bedWeaveBedtime: "Tejer un cuento de buenas noches",
    bedAnother: "Otro",
    bedNarrating: "Afinando...",
    bedShareGift: "Compartir un Momento Tranquilo",
    bedWrapping: "Envolviendo...",
    bedGiftReady: "Un regalo suave está listo ✿",
    bedGiftBody: "Compártelo con abuelos o familia — no necesitan registrarse para leerlo o escucharlo.",
    bedCopyLink: "Copiar enlace",
    bedCopied: "Copiado",
    bedLinkCopied: "Enlace copiado con suavidad ✿",
    bedCouldNotWeave: "No pudimos tejer aún",
    bedCouldNotGift: "No pudimos preparar el regalo aún",

    // Emotions
    emoBackHome: "Volver al inicio",
    emoTitle: "¿Cómo te sientes?",
    emoSubtitle: "Cada sentimiento es bienvenido aquí.",
    emoSavedThanks: "Gracias por compartir ✿",
    emoSaveFailed: "No se pudo guardar",
    emoSave: "Guardar con calma",
    emoCalmingBreath: "Una respiración tranquila",
    emoBreatheBody: "Mira a la abejita girar y respira con ella.",
    emoBreathIn: "Inhala...",
    emoBreathOut: "Exhala suavemente...",
    emoTapBegin: "Toca para comenzar",
    emoPauseGently: "Pausa suave",
    emoBeginBreath: "Comenzar a respirar",

    // Garden
    gardenBackHome: "Volver al inicio",
    gardenTitle: "Tu Jardín",
    gardenSubtitle: "Cada actividad suave hace crecer una flor.",
    gardenFlower: "flor",
    gardenFlowers: "flores",
    gardenWaiting: "Tu jardín espera. Prueba una actividad suave para hacer crecer tu primera flor.",
    gardenByCategory: "Por categoría",
    gardenWillGather: "El progreso se reunirá aquí conforme juegues.",
    gardenTimes: "veces",
    gardenTotalMinutes: "Minutos totales:",
    gardenCat_letters: "Letras",
    gardenCat_numbers: "Números",
    gardenCat_shapes: "Formas",
    gardenCat_colors: "Colores",
    gardenCat_emotions: "Sentimientos",
    gardenCat_mindfulness: "Atención plena",
    gardenCat_creativity: "Creatividad",
    gardenCat_nature: "Naturaleza",
    gardenCat_music: "Música",
    gardenCat_stories: "Cuentos",
    gardenCat_focus: "Atención",
    gardenCat_kindness: "Bondad",
    gardenCat_bedtime: "Hora de dormir",

    // Membership
    memTitle: "Cuiden la colmena juntos",
    memSubtitle: "BeeNest se construye sin anuncios ni bucles adictivos. Tu apoyo la mantiene suave.",
    memActiveBadge: "Miembro activo ✿",
    memMonthly: "Mensual",
    memYearly: "Anual",
    memPerMonth: "/ mes",
    memPerYear: "/ año",
    memSavePct: "Ahorra 25 %",
    memChoose: "Elegir",
    memOpening: "Abriendo el pago...",
    memCheckoutFailed: "No se pudo abrir el pago",
    memPerk1: "Cuentos de buenas noches con IA, sin límite",
    memPerk2: "Todas las herramientas sensoriales",
    memPerk3: "Perfiles para varios niños/as",
    memPerk4: "Miradas al bienestar",
    memPerkY1: "Todo lo del plan Mensual",
    memPerkY2: "Soporte prioritario",
    memPerkY3: "Hojas de actividades imprimibles",
    memPerkY4: "Acceso temprano a nuevas herramientas",

    // MembershipSuccess
    msChecking: "Confirmando tu apoyo gentil...",
    msCheckingBody: "Solo tomará un momento.",
    msPaid: "Bienvenido/a a la colmena completa ✿",
    msPaidBody: "Tu membresía está activa. Gracias por cuidar este nido.",
    msBackToNest: "Volver a tu nido",
    msExpired: "La sesión descansó demasiado",
    msTryAgain: "Inténtalo de nuevo con calma",
    msQuietMoment: "Un momento tranquilo",
    msQuietBody: "Seguiremos comprobando. Puedes volver a tu nido cuando quieras.",
    msBackDashboard: "Volver al panel",

    // GiftStory
    giftLabel: "Un Momento Tranquilo ✿",
    giftFrom: "De",
    giftTo: "para",
    giftPause: "Pausar narración",
    giftListen: "Escuchar con calma",
    giftNotFound: "Este momento tranquilo no se encuentra.",
    giftVisit: "Visitar BeeNest",
    giftFooterBody: "Este cuento fue tejido en BeeNest — una colmena tranquila y amigable con los sentidos, de aprendizaje y bienestar para los pequeños.",
    giftDiscover: "Descubre BeeNest",

    // LetterDetail
    ldAllLetters: "Todas las cartas",
    ldPrint: "Imprimir / Guardar PDF",
    ldFamilyReflection: "BeeNest · Reflexión Familiar",
    ldForHive: "Para tu colmena gentil",
    ldFooter: "Cada momento gentil importa · BeeNest se siente honrado de acompañar el camino de tu familia.",
    ldNoteTitle: "Una pequeña nota de tu familia",
    ldNoteIntro: "Guarda una nota privada en esta carta — un recuerdo, una pequeña alegría, una pequeña preocupación. Se queda solo contigo.",
    ldNotePlaceholder: "Un recuerdo o pensamiento tierno...",
    ldNoteSave: "Guardar nota",
    ldNoteSaving: "Guardando...",
    ldNoteSaved: "Nota guardada en la carta ✿",
    ldNoteSaveFailed: "Aún no pudimos guardar",

    // Settings
    setSubtitle: "Personaliza la colmena para los sentidos de tu familia.",
    setSensory: "Sensorial",
    setLanguageSection: "Idioma",
    setAppLanguage: "Idioma de la app",
    setBedtimeSection: "Modo noche",
    setBedtimeDimmed: "Paleta tenue color miel-noche",
    setBedtimeDesc: "Fondo índigo más suave, texto más cálido.",
    setRemSection: "Recordatorios de sueño",
    setRemIntro: "Opcional y siempre suave. Una invitación gentil, nunca una exigencia. Apagado por defecto.",
    setRemEnable: "Activar recordatorios suaves",
    setRemEnableDesc: "Un único aviso suave cerca de la hora de dormir. Fácil de apagar cuando quieras.",
    setRemTime: "Hora del recordatorio",
    setRemQuietDays: "Días tranquilos (sin recordatorio)",
    setRemBrowserTitle: "Notificación del navegador también",
    setRemBrowserDesc: "Opcional. Una única notificación silenciosa cuando la pestaña está abierta.",
    setRemBrowserDenied: "El permiso del navegador no fue concedido — los recordatorios se quedarán solo dentro de la app.",
    setRemPreviewLabel: "Vista previa:",
    setRemPreviewQuote: "Tu colmena podría estar lista para un suave reinicio.",
    setRemPreviewMeta: "Sin urgencia, sin rachas, sin culpa. Los recordatorios son solo una invitación suave.",
    setPauseSection: "Pausa de Reflexión Suave (para ti)",
    setPauseIntro: "BeeNest también cuida a quien cuida. Cerca de una vez por semana puede aparecer en tu panel una invitación suave y opcional. Puedes comenzar una cuando quieras, o desactivarla por completo.",
    setPauseEnable: "Activar invitaciones de Pausa de Reflexión",
    setPauseEnableDesc: "Invitación suave semanal — nunca urgente, siempre fácil de cerrar.",
    setPauseBeginNow: "Comenzar una pausa ahora",
    setMon: "lun", setTue: "mar", setWed: "mié", setThu: "jue", setFri: "vie", setSat: "sáb", setSun: "dom",
  },
};

export const t = (lang, key) =>
  (STRINGS[lang] && STRINGS[lang][key]) || STRINGS.en[key] || key;

export default t;
